import React from "react"
import { Box } from "@chakra-ui/react"

import { cn } from "@/lib/utils"

import { BROKER_ALERT_DESC_CLASS } from "./brokerAlertUi"
import { useBrokerGuidanceColors } from "./BrokerGuidancePanel"
import {
  ChecklistRow,
  GuidanceBlock,
  GuidanceIntro,
} from "./ibkrGuidanceShared"

export interface CtraderCsvStatementColumnGuidanceProps {
  className?: string
  /** When true, lead with “re-export” wording (CSV import tab). */
  forCsvImport?: boolean
}

/**
 * cTrader History → Statement CSV: columns users must enable in the cTrader UI before each export.
 * Compose inside GuidanceAccordionBody when used in a collapsible panel.
 */
export function CtraderCsvStatementColumnGuidance({
  forCsvImport = false,
}: CtraderCsvStatementColumnGuidanceProps): React.ReactElement {
  const colors = useBrokerGuidanceColors()

  return (
    <>
      <GuidanceIntro color={colors.bodyColor} className="!mb-0">
        {forCsvImport ? (
          <>
            In cTrader, open{" "}
            <Box as="span" fontWeight="semibold">
              History
            </Box>{" "}
            in Trade Watch, set your date range, then click{" "}
            <Box as="span" fontWeight="semibold">
              Statement
            </Box>{" "}
            and save as CSV. The file must include a{" "}
            <Box as="span" fontWeight="semibold">
              Deals
            </Box>{" "}
            section with closed trades.{" "}
          </>
        ) : null}
        Before exporting, open{" "}
        <Box as="span" fontWeight="semibold">
          statement column settings
        </Box>{" "}
        in the cTrader UI (right-click any History column header) and enable the columns below.
        cTrader only exports columns that are visible in the grid. You must do this manually each
        time you export.
      </GuidanceIntro>

      <GuidanceBlock title="Required columns">
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Order ID
          </Box>
          : numeric; used as stable trade identity
        </ChecklistRow>
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Opening Time
          </Box>
          : with timezone suffix, e.g. Opening Time (UTC+3) (match Closing Time&apos;s offset)
        </ChecklistRow>
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Closing Time
          </Box>
          : usually already present in default exports
        </ChecklistRow>
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Net USD
          </Box>{" "}
          (or Net EUR, etc.): net P&amp;L per deal
        </ChecklistRow>
      </GuidanceBlock>

      <GuidanceBlock title="Strongly recommended">
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Gross USD
          </Box>
          : separate gross vs net on the dashboard
        </ChecklistRow>
        <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
          <Box as="span" fontWeight="semibold">
            Commissions
          </Box>
          : accurate exit fees
        </ChecklistRow>
        <p className={cn(BROKER_ALERT_DESC_CLASS, "!mb-0")}>
          Deal times use the offset in the column headers (for example Opening Time (UTC+3)).
          Tessera converts them to UTC automatically.
        </p>
      </GuidanceBlock>
    </>
  )
}

export default CtraderCsvStatementColumnGuidance
